import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GroupedVirtuoso } from 'react-virtuoso-next';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '~/store/actions';
import { Close } from '@material-ui/icons';
import * as Loaders from '~/components/Loaders';
import { GroupTypesEnum, ConversationFilterEnum } from '~/enums/conversations';
import { Conversation, ConversationList } from '~/interfaces/conversations';
import { _, ReactSVG, useTranslation } from '~/dependencies';
import {
  BulkActionIcon,
  IconNoConversations,
  NoSearchResultIcon,
} from '~/assets/svgIcons';
import { RootState } from '~/store/config';
import { CircularLoader } from '~/components/CallHistory/styles';
import Row from '../Row';
import RoundedCheckbox from '../RoundedCheckbox';
import {
  Container,
  Empty,
  FiltersContainer,
  Searcher,
  Section,
  TextField,
  ButtonsContainer,
  Button,
  FooterLoader,
  SearchBar,
  AllConversationsSelector,
  AllMessagesLoaded,
} from './styles';

type ListProps = {
  data: ConversationList;
};

/**
 * Custom wrapper for react-virtuoso's sticky group headers (the "top item
 * list"). The library renders this element with `position: sticky; top: 0` but
 * WITHOUT a z-index, so it sits below the rows' Material-UI badges
 * (`.MuiBadge-badge` has `z-index: 1`) and the unread counter / channel icon of
 * a row scrolled underneath paints over the header. `position: sticky` creates
 * its own stacking context, so a z-index on the inner group content can't fix
 * this — it has to go on this wrapper. See the `TopItemList` component docs.
 */
const StickyGroupHeaderList = React.forwardRef<
  HTMLDivElement,
  // eslint-disable-next-line react/require-default-props
  { style?: React.CSSProperties; children?: React.ReactNode }
>(({ style, children, ...props }, ref) => (
  <div ref={ref} {...props} style={{ ...style, zIndex: 2 }}>
    {children}
  </div>
));
StickyGroupHeaderList.displayName = 'StickyGroupHeaderList';

const EmptyConversation = ({ isSearch }: { isSearch: boolean }) => {
  const { t } = useTranslation();

  return (
    <Empty>
      <ReactSVG
        src={isSearch ? NoSearchResultIcon : IconNoConversations}
        className="icon"
      />
      <span className="title">
        {isSearch
          ? t('screens.conversation.noResultFound')
          : t('screens.conversation.noMessages')}
      </span>
      <span className="subTitle">
        {isSearch
          ? t('screens.conversation.noResultFoundSubTitle')
          : t('screens.conversation.noMessagesSubTitle')}
      </span>
    </Empty>
  );
};

const List = ({ data }: ListProps) => {
  // REFs
  const virtuoso = useRef(null);
  // Tracks whether the user has scrolled away from the top at least once, so we
  // don't treat the initial "at top" state (fired on mount) as a scroll-to-top.
  const hasScrolledAwayFromTop = useRef(false);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // SELECTORS
  const {
    isLoading,
    filters,
    totalSize,
    openSelection: openSelector,
    lastReadIndex,
  } = useSelector((state: RootState) => state.conversation);

  // FIXME: Change to useTypedSelector and remove RootState
  const isAdmin = useSelector((state: RootState) => state.auth?.isAdmin);

  // CONST'S
  const { type } = filters;

  const buttons = [
    {
      name: ConversationFilterEnum.MY_MESSAGES,
      testId: 'all-messages-filter',
      text: isAdmin
        ? t('screens.conversation.filterAllMessagesTxt')
        : t('screens.conversation.filterMyMessagesTxt'),
    },
    {
      name: ConversationFilterEnum.UNREAD,
      testId: 'unread-filter',
      text: t('screens.conversation.filterUnreadTxt'),
    },
    ...(isAdmin
      ? [
          {
            name: ConversationFilterEnum.UNASSIGNED,
            testId: 'unassigned-filter',
            text: t('screens.conversation.filterUnassignedTxt'),
          },
        ]
      : []),
    {
      name: ConversationFilterEnum.ARCHIVED,
      testId: 'archived-filter',
      text: t('screens.conversation.filterArchivedTxt'),
    },
  ];

  const shouldShowEmpty = !isLoading && data.length === 0;
  const shouldShowListLoader = data.length === 0 && isLoading;

  // STATE's
  const [selectedName, setSelectedName] = useState(type);
  const [search, setSearch] = useState<string>('');
  const [isSearchInputEmpty, setIsSearchInputEmpty] = useState<boolean>(false);

  const searchValue = isSearchInputEmpty
    ? search
    : search || filters.search || '';

  const conversationParsed = _(data)
    .orderBy((e: Conversation) => e.groupType)
    .value();

  // @ts-ignore
  const listGrouped = _(conversationParsed)
    .groupBy((conversation: Conversation) => conversation.groupType)
    .value();
  const groupCounts = Object.values(listGrouped).map(
    // @ts-ignore
    conversation => conversation.length
  );

  const groups = Object.keys(listGrouped);

  useEffect(() => {
    /**
     * Remember last item position the user has clicked to read/open the chat with
     */
    if (lastReadIndex) {
      // @ts-ignore
      virtuoso.current?.scrollToIndex({
        index: lastReadIndex + 1, // Flat item index, +1 is to adjust the position and return the user to the exact same place
        align: 'start',
      });
      dispatch(actions.setConversationLastReadIndex(0));
    }
  }, [dispatch, groupCounts, lastReadIndex]);

  const renderGroup = (index: number) => {
    const title =
      groups[index] === GroupTypesEnum.LATEST_MESSAGES
        ? t('screens.conversation.latestMessagesTxt')
        : t('screens.conversation.immediateActionTxt');

    return (
      <Section scrollClearfix={lastReadIndex}>
        <span>{title}</span>
      </Section>
    );
  };

  const renderItem = (index: number) => {
    const item = conversationParsed[index];
    if (item) {
      return <Row index={index} item={item} />;
    }
    return null;
  };

  const handleBulkAction = (open: boolean) => {
    // Don't enter selection mode when there are no conversations to act on.
    if (open && data.length === 0) {
      return;
    }
    dispatch(actions.openConversationSelection(open));
  };

  const setServicesValueDebounced = useCallback(
    _.debounce(
      (value: string) => dispatch(actions.setConversationSearch(value)),
      1000
    ),
    []
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setIsSearchInputEmpty(value.length === 0);
    setSearch(value);
    setServicesValueDebounced(value);
  };

  const resetSearch = () => {
    setSearch('');
    dispatch(actions.setConversationSearch(undefined));
  };

  const handleSelectAllConversations = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const marked = event.target.checked;
    dispatch(actions.selectAllConversations(marked));
  };

  /**
   * `startReached` from react-virtuoso is throttled and frequently drops the
   * final emission when scrolling stops exactly at the top, so it never fires
   * reliably. `atTopStateChange` fires deterministically whenever the list
   * transitions to/from the top, which is what we actually want here.
   */
  const handleAtTopStateChange = (atTop: boolean) => {
    // Skip the "at top" that fires on mount before the user has scrolled.
    if (!hasScrolledAwayFromTop.current) {
      if (!atTop) {
        hasScrolledAwayFromTop.current = true;
      }
      return;
    }

    if (atTop && !openSelector && !isLoading) {
      dispatch(actions.setConversationPage(1));
      dispatch(actions.getAllConversations());
    }
  };

  const handleEndReached = () => {
    if (data.length > 8) {
      dispatch(
        actions.setConversationPage(filters.page ? filters.page + 1 : 1)
      );
      dispatch(actions.getAllConversations());
    }
  };

  const handleFilterType = (filterType: ConversationFilterEnum | undefined) => {
    if (filterType) {
      setSelectedName(filterType);

      switch (filterType) {
        case ConversationFilterEnum.MY_MESSAGES:
          dispatch(
            actions.setConversationFilter({
              type: ConversationFilterEnum.MY_MESSAGES,
              is_active: 1,
              page: 1,
              unread: false,
              unassigned: false,
            })
          );
          break;
        case ConversationFilterEnum.UNREAD:
          dispatch(
            actions.setConversationFilter({
              type: ConversationFilterEnum.UNREAD,
              is_active: 1,
              page: 1,
              unread: true,
              unassigned: false,
            })
          );
          break;
        case ConversationFilterEnum.UNASSIGNED:
          dispatch(
            actions.setConversationFilter({
              type: ConversationFilterEnum.UNASSIGNED,
              is_active: 1,
              page: 1,
              unassigned: true,
              unread: false,
            })
          );
          break;
        case ConversationFilterEnum.ARCHIVED:
          dispatch(
            actions.setConversationFilter({
              type: ConversationFilterEnum.ARCHIVED,
              is_active: 0,
              page: 1,
              unread: false,
              unassigned: false,
            })
          );
          break;
        default:
      }
    }
  };

  return (
    <>
      <FiltersContainer>
        <Searcher>
          <SearchBar>
            <TextField
              id="search"
              data-test-id="search-contact"
              fullWidth
              value={searchValue}
              placeholder={t('screens.conversation.searchFieldTitle')}
              variant="outlined"
              size="small"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSearch(e)
              }
            />
            {searchValue.length > 0 && (
              <Close className="icon" fontSize="small" onClick={resetSearch} />
            )}
          </SearchBar>
          <button
            type="button"
            aria-label="Bulk Actions"
            className="btn-bulk-action"
            onClick={() => handleBulkAction(!openSelector)}>
            <ReactSVG src={BulkActionIcon} />
          </button>
        </Searcher>
        <ButtonsContainer isAdmin={isAdmin}>
          {buttons.map(btnInfo => (
            <Button
              key={btnInfo.name}
              data-test-id={btnInfo.testId}
              variant="outlined"
              isSelected={selectedName === btnInfo.name}
              onClick={() => handleFilterType(btnInfo.name)}>
              {btnInfo.text}
            </Button>
          ))}
        </ButtonsContainer>
      </FiltersContainer>

      <Container>
        {openSelector && data.length > 0 && (
          <AllConversationsSelector>
            <RoundedCheckbox onChange={handleSelectAllConversations} />
            <span>{t('screens.conversation.selectAllConversations')}</span>
          </AllConversationsSelector>
        )}
        {data.length > 0 && (
          <GroupedVirtuoso
            ref={virtuoso}
            groupCounts={groupCounts}
            groupContent={renderGroup}
            itemContent={renderItem}
            style={{
              flex: 1,
            }}
            atTopStateChange={handleAtTopStateChange}
            endReached={handleEndReached}
            components={{
              TopItemList: StickyGroupHeaderList,
              Footer: () => (
                <FooterLoader>
                  {isLoading ? (
                    <CircularLoader size={30} thickness={2} />
                  ) : null}
                  {totalSize === data.length && !isLoading ? (
                    <AllMessagesLoaded>
                      {t('screens.conversation.allConversationsLoaded')}
                    </AllMessagesLoaded>
                  ) : null}
                </FooterLoader>
              ),
            }}
          />
        )}
        {shouldShowEmpty && <EmptyConversation isSearch={search.length > 0} />}
        {shouldShowListLoader ? (
          <Loaders.List
            numRows={10}
            style={{
              width: 370,
              marginLeft: 'unset',
              marginRight: 'unset',
            }}
          />
        ) : null}
      </Container>
    </>
  );
};

export default List;
